import { useCallback, useEffect, useState, useMemo } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { prop } from 'ramda';
import { ONE_YEAR_SECONDS, compact } from '@moda/portal-stanchions';
import { reportError } from '../../lib/reportError';
import { getVariantClickHistory } from '../../lib/variantClicksHistory';
import { tc } from '../../lib/trackingContext';
import { useCountry } from '../../components/CountrySelector';
import { useCartVersion } from '../useCart';
import { GET_CART_QUERY } from '../useCart/cart.rest';
import { useCookies } from '../useCookies';
import { AddToBagFragment } from '../../generated/types';
import { ADD_SET_TO_BAG_MUTATION } from './addSetToBag.rest';

export enum AddSetToBagMode {
  Resting = 'Add Set To Bag',
  Pending = 'Adding Set to Bag',
  Added = 'Added to cart!',
  Error = 'Error Adding Set to Bag'
}

export type Item = {
  product_id: number;
  variant_id: string;
  cart_id: string | null;
  country_code: string;
  position: number;
  size: string;
  quantity: number;
  customization?: string | null;
  isCustomizationValid?: boolean | null;
  href?: string | null;
  onAdded?: () => void;
};

export type EditItem = {
  variantId: number;
  size?: string;
  quantity?: number;
};

const SHOW_ADDED_MS = 1000;
const SHOW_ERROR_MS = 2000;

const getButtonText = (mode: AddSetToBagMode, items: Item[]) => {
  if (items.length === 0) return 'Sold Out';

  return mode;
};

export const useAddSetToBag = (
  variants: AddToBagFragment[],
  restingText: string,
  editItems?: EditItem[]
) => {
  const [mode, setMode] = useState<AddSetToBagMode>(restingText as AddSetToBagMode);

  const { country } = useCountry();
  const {
    cookies: { cartId },
    setCookie
  } = useCookies();
  const client = useApolloClient();
  const cartVersion = useCartVersion();

  const availableVariants = useMemo(
    () => variants.filter(variant => variant.masterVariant.inventory.some(size => size?.canSell)),
    [variants]
  );

  const queryID =
    compact(availableVariants.map(variant => getVariantClickHistory(variant.id)))[0]?.queryId ??
    availableVariants[0]?.queryid;

  const items: Item[] = useMemo(() => {
    if (editItems && editItems.length > 0) {
      return editItems
        .map(editItem => {
          const variant = availableVariants.find(
            variantItem => Number(variantItem.id) === editItem.variantId
          );
          if (!variant) return null;

          const fallbackSize =
            variant.masterVariant.inventory.find(size => size?.canSell)?.size || 'OS';

          const item: Item = {
            product_id: variant.productId,
            variant_id: variant.id,
            cart_id: cartId || null,
            country_code: country.alpha2Code,
            position: 1,
            size: editItem.size || fallbackSize,
            quantity: editItem.quantity ?? 1
          };

          return item;
        })
        .filter((item): item is Item => item !== null);
    }

    return availableVariants.map(variantItem => ({
      product_id: variantItem.productId,
      variant_id: variantItem.id,
      cart_id: cartId || null,
      country_code: country.alpha2Code,
      position: 1,
      size: variantItem.masterVariant.inventory.find(size => size?.canSell)?.size || 'OS',
      quantity: 1
    }));
  }, [availableVariants, cartId, country.alpha2Code, editItems]);

  const [addSetToBag, { loading }] = useMutation(ADD_SET_TO_BAG_MUTATION, {
    onCompleted: data => {
      setMode(AddSetToBagMode.Added);
      data?.addSetToBag?.data?.map(cart => {
        tc.track('Product Added', {
          addToScopeOnce: {
            products: cart?.attributes?.cartItems.map(prop('data'))
          },
          addToTrackingEvent: {
            label: cart?.id as string,
            eventType: 'conversion',
            index: availableVariants[0].index,
            queryID,
            objectIDs: availableVariants.map(variant => variant.objectid)
          }
        });
      });
    },
    onError: error => {
      reportError('ADD_SET_TO_BAG', error);
      setMode(AddSetToBagMode.Error);
    },
    update: (cache, { data }) => {
      if (!data) return;

      const newCartId = data.addSetToBag.data[0].id;

      if (!newCartId) return;

      if (!cartId) {
        setCookie('cartId', newCartId, ONE_YEAR_SECONDS);
      }

      cache.writeQuery({
        query: GET_CART_QUERY,
        variables: { cartId: newCartId, cartVersion, countryCode: country.alpha2Code },
        data: { userCart: { ...data.addSetToBag, __typename: 'RestUserCart' } }
      });
      tc.setCurrent('cart', data.addSetToBag.data[0]);
    }
  });

  const handleAddSetToBag = useCallback(async () => {
    setMode(AddSetToBagMode.Pending);

    if (cartId) {
      // make sure the cart is fetched before running the mutation
      await client.query({
        query: GET_CART_QUERY,
        variables: { cartId, cartVersion, countryCode: country.alpha2Code }
      });
    }

    addSetToBag({
      variables: {
        input: {
          data: {
            attributes: {
              items
            },
            type: 'line_items'
          }
        }
      }
    });
  }, [addSetToBag, cartId, cartVersion, client, country.alpha2Code, items]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (mode === AddSetToBagMode.Added) {
      timeout = setTimeout(() => setMode(restingText as AddSetToBagMode), SHOW_ADDED_MS);
    }

    return () => clearTimeout(timeout);
  }, [mode, restingText]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (mode === AddSetToBagMode.Error) {
      timeout = setTimeout(() => setMode(restingText as AddSetToBagMode), SHOW_ERROR_MS);
    }

    return () => clearTimeout(timeout);
  }, [mode, restingText]);

  const text = getButtonText(mode, items);

  return {
    added: mode === AddSetToBagMode.Added,
    disabled: mode !== restingText || items.length === 0,
    error: mode === AddSetToBagMode.Error,
    handleAddSetToBag,
    loading,
    text
  };
};
